import type React from "react";
import { useAppSelector } from "../../hooks/redux-hooks";
import AppointmentSelectionModal from "../TransferButton/AppointmentSelectionModal";
import MFACodeModal from "../TransferButton/MFACodeModal";
import { useTransfer } from "../TransferButton/TransferContext";
import { useTransferReadinessGate } from "../TransferButton/useTransferReadinessGate";

// Mounts the appointment / note-template / MFA modals at the app shell level
// so the context state survives navigation. Modals only render while the
// clinician is on the target note's page — elsewhere the banner's "Go to note"
// CTA brings them here, and the modal pops once they land.
const TransferModalsHost: React.FC = () => {
  const {
    activeJob,
    appointmentRows,
    appointmentsRefreshedAt,
    refreshAppointments,
    showAppointmentModal,
    isSelectingAppointment,
    isNoteTypeSelection,
    showMFAModal,
    isSubmittingMFA,
    mfaOptions,
    selectAppointment,
    cancelAppointment,
    submitMFACode,
    cancelMFA,
  } = useTransfer();
  const { notesDetails } = useAppSelector((state) => state.notes);
  const { selectedClient } = useAppSelector((state) => state.appState);

  const isOnTargetNote = activeJob?.audio_file_id != null && notesDetails?.id === activeJob.audio_file_id;
  const pickerVisible = showAppointmentModal && isOnTargetNote;

  // Readiness rides the existing per-note status poll; only the picker's
  // confirm button gates on it (capped at 15s from modal-open).
  const { confirmGated } = useTransferReadinessGate(activeJob?.audio_file_id ?? null, pickerVisible);

  // The department picker reuses the appointment modal (awaiting_appointment) —
  // the options are tagged kind:"department" so we can title it correctly.
  const isDepartmentSelection = appointmentRows.some((row) => row.option.kind === "department");

  const modalTitle = isNoteTypeSelection
    ? "Select Note Template"
    : isDepartmentSelection
      ? "Select Department"
      : undefined;
  const modalDescription = isNoteTypeSelection
    ? "Multiple note templates are available. Please select the template to use for this note:"
    : isDepartmentSelection
      ? "Please select the department to write this note into:"
      : undefined;

  return (
    <>
      <AppointmentSelectionModal
        visible={pickerVisible}
        appointments={appointmentRows}
        onSelect={selectAppointment}
        onCancel={cancelAppointment}
        loading={isSelectingAppointment}
        title={modalTitle}
        description={modalDescription}
        confirmGated={confirmGated}
        refreshedAt={appointmentsRefreshedAt}
        patientMrn={selectedClient?.mrn ?? null}
        onRetryFetch={refreshAppointments}
      />
      <MFACodeModal
        visible={showMFAModal && isOnTargetNote}
        onSubmit={submitMFACode}
        onCancel={cancelMFA}
        loading={isSubmittingMFA}
        mfaOptions={mfaOptions}
      />
    </>
  );
};

export default TransferModalsHost;
